<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';
import type {
  InitialData as NewAirRoadUnpackDialogFormInitialData,
  ValidData as NewAirRoadUnpackDialogFormValidData,
} from '@/components/dialogForms/NewAirRoadUnpackDialogForm.vue';

import { computed, onMounted, useTemplateRef } from 'vue';

import { setAutofocus } from '@/utils/html';
import KBtn from '@/components/btns/KBtn.vue';
import NewAirRoadUnpackDialogForm from '@/components/dialogForms/NewAirRoadUnpackDialogForm.vue';
import ReportGenerateDialogHeader from '@/components/headers/ReportGenerateDialogHeader.vue';
import PageDialogWrapper from '@/components/wrappers/PageDialogWrapper.vue';

export interface Props {
  isConfirmLoading?: boolean;
  confirm?: (
    packageNum: number,
    productNum?: number,
    addProductId?: number,
    formData?: NewAirRoadUnpackDialogFormValidData,
  ) => Promise<unknown>;
  packageNum?: number;
  productNum?: number;
  allUnpackCount?: number;
  addProductId?: number;
  maxQuantity?: number;
}
export type DefProps = typeof props;
const props = withDefaults(defineProps<Props>(), {
  isConfirmLoading: false,
  confirm: undefined,
  packageNum: 0,
  productNum: 0,
  allUnpackCount: undefined,
  addProductId: undefined,
  maxQuantity: undefined,
});
const emit = defineEmits<{
  cancelBtnClick: [event: Event];
  confirmBtnClick: [
    event: Event | SubmitEventPromise | null | undefined,
    formData: NewAirRoadUnpackDialogFormValidData | undefined,
    props: DefProps,
  ];
}>();

function handleCancelClick(event: Event): void {
  // also it should be wrapped with <v-dialog :persistent="isConfirmLoading"
  if (props.isConfirmLoading) return;
  emit('cancelBtnClick', event);
}

function handleSubmit(
  event: Event | SubmitEventPromise | null | undefined,
  formData?: NewAirRoadUnpackDialogFormValidData,
): void {
  if (props.isConfirmLoading) return;
  emit('confirmBtnClick', event, formData, props);
}

const mainEl = useTemplateRef('main');

function getSubmitBtn(): HTMLInputElement | null {
  if (!mainEl.value) return null;
  return mainEl.value.querySelector('input[type=submit]');
}

function handleConfirmClick(event: Event): void {
  if (props.allUnpackCount != null) {
    handleSubmit(event);
  } else {
    const submitBtn = getSubmitBtn();
    if (!submitBtn) return;
    submitBtn.click();
  }
}

const initialData = computed<NewAirRoadUnpackDialogFormInitialData>(() => {
  return {
    quantity: props.maxQuantity || 1,
  };
});

const footerMenuEl = useTemplateRef('footerMenu');

onMounted(function () {
  setAutofocus(footerMenuEl.value);
});
</script>

<template>
  <PageDialogWrapper
    :class="$style.newAirRoadUnpackDialog"
    data-testid="newAirRoadUnpackDialog"
  >
    <template #header>
      <ReportGenerateDialogHeader @close-click="handleCancelClick">
        <template v-if="props.allUnpackCount != null">Unpack all</template>
        <template v-else>
          <template v-if="props.addProductId">Add products</template>
          <template v-else>Unpack products</template>
        </template>
      </ReportGenerateDialogHeader>
    </template>
    <section ref="main" :class="$style.main">
      <div v-if="props.allUnpackCount != null" :class="$style.text">
        Unpack the package with {{ allUnpackCount }} item<template
          v-if="allUnpackCount > 1"
          >s</template
        >.
      </div>
      <NewAirRoadUnpackDialogForm
        v-else
        :initial-data="initialData"
        :max-quantity="props.maxQuantity"
        :loading="props.isConfirmLoading"
        @submit="handleSubmit"
      />
    </section>
    <template #footer>
      <menu ref="footerMenu" :class="$style.dialogFooterMenuList">
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            variant="outlined"
            append-icon="mdi-close"
            :class="$style.btn"
            @click="handleCancelClick"
          >
            Cancel
          </KBtn>
        </li>
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            append-icon="mdi-check"
            :class="$style.btn"
            :loading="props.isConfirmLoading"
            :disabled="!props.confirm"
            autofocus
            @click="handleConfirmClick"
          >
            <template v-if="props.allUnpackCount != null">Unpack</template>
            <template v-else>
              <template v-if="props.addProductId">Add product</template>
              <template v-else>Unpack</template>
            </template>
          </KBtn>
        </li>
      </menu>
    </template>
  </PageDialogWrapper>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use 'sass:math';
@use '@/styles/colors';
@use '@/styles/variables';

.newAirRoadUnpackDialog {
  width: 420px;
}
  .main {
  }
  .text {
    margin: 0 0 math.div(variables.$pageDialogWindowMainWrapperMargin, 2) 0;
  }
  .dialogFooterMenuList {
    list-style: none;
    margin: 0;
    padding: 0;
    text-align: right;
  }
    .dialogFooterMenuListItem {
      display: inline;
    }
    .dialogFooterMenuListItem + .dialogFooterMenuListItem {
      margin-left: 1rem;
    }
      .btn {
      }
</style>
